import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash, faFilter, faSync, faLayerGroup, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import PaginatedItems from '../../../../components/administracao/Pagination';
import RequireRoles from '../../../../components/RequireRoles';
import useFetch from '../../../../hooks/useFetch';
import Swal from 'sweetalert2';

export default function GerenciarCategorias() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        categorias: [],
        count: 0,
        filtros: { nome_categoria: "" },
        filtroAtivo: false,
        loading: true,
        mostrarModal: false,
        registroAExcluir: {}
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [categorias, count] = await buscarCategorias({});
            setState(s => ({ ...s, categorias, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarCategorias(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") query = query.concat(key, "=", filtros[key], "&");
            }
        }
        try {
            let response = await get('/categoriafotografia' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarCategorias():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [categorias, count] = await buscarCategorias(filtros);
        setState({ ...state, categorias, count, loading: false });
    }

    const filtrar = async () => {
        let [categorias, count] = await buscarCategorias(state.filtros);
        setState(s => ({ ...s, categorias, count, filtroAtivo: true, loading: false }));
    };

    const limpar = async () => {
        let [categorias, count] = await buscarCategorias({});
        setState(s => ({ ...s, filtros: defaultState.filtros, categorias, count, filtroAtivo: false, loading: false }));
    };

    async function excluirCategoria() {
        try {
            let response = await del('/categoriafotografia/' + state.registroAExcluir.id_categoria);
            if (response.status === 200) {
                let [categorias, count] = await buscarCategorias(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, categorias, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({
                    title: "Não foi possível excluir a categoria",
                    text: response.result?.message ?? "Esta categoria tem subcategorias vinculadas.",
                    icon: "error",
                    confirmButtonColor: "#587b7f",
                });
            } else {
                console.error('Erro no excluirCategoria()');
                Swal.fire({ title: "Não foi possível excluir a categoria", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirCategoria():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Categorias Iconográficas</h2>
                    <p className="admin-subtitle">Organize o acervo por grandes grupos temáticos</p>
                </div>
                <Link to="/admin/iconografico/categorias/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Categoria
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form className="row g-3" onSubmit={(e) => { e.preventDefault(); filtrar(); }}>
                        <Form.Group className="col-md-9">
                            <Form.Label className="label-admin">Nome da Categoria</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Filtrar por nome (ex: Arquitetura)..."
                                value={state.filtros.nome_categoria}
                                onChange={e => setState({ ...state, filtros: { nome_categoria: e.target.value } })}
                            />
                        </Form.Group>
                        <div className="col-md-3 d-flex gap-2 align-items-end">
                            <Button type="button" className="btn-apply-filters w-100" onClick={filtrar}>
                                <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                            </Button>
                            <Button type="button" className="btn-clear-filters" onClick={limpar}>
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome da Categoria</th>
                                <th>Descrição</th>
                                <th className="text-center">Subcategorias</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.categorias.length > 0 ? state.categorias.map((item) => (
                                <tr key={item.id_categoria}>
                                    <td data-label="Nome da Categoria" className="fw-bold">
                                        <FontAwesomeIcon icon={faFolderOpen} className="me-2 text-muted opacity-50" />
                                        {item.nome_categoria}
                                    </td>
                                    <td data-label="Descrição" className="text-muted small">{item.descricao || '-'}</td>
                                    <td data-label="Subcategorias" className="text-center">
                                        <Link
                                            to={`/admin/iconografico/categorias/${item.id_categoria}/subcategorias`}
                                            className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-bold"
                                        >
                                            <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                                            Gerenciar Subs
                                        </Link>
                                    </td>
                                    <td data-label="Opções">
                                        <div className="d-flex justify-content-center gap-2">
                                            <Link className="action-icon edit" to={`/admin/iconografico/categorias/${item.id_categoria}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                            <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                            </RequireRoles>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" className="text-center py-4">Nenhuma categoria encontrada.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.categorias.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Categoria</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir a categoria <strong>{state.registroAExcluir.nome_categoria}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirCategoria}>Confirmar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
